import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CreditCard, MessageCircle, Mail, Phone, KeyRound, QrCode, Handshake, ShieldCheck, UserPlus, Loader2, FileText } from "lucide-react";
import { adminApi, apiErr } from "@/lib/authApi";

const KIND = {
  registration: { icon: UserPlus, color: "var(--text)" },
  payment: { icon: CreditCard, color: "var(--green)" },
  whatsapp: { icon: MessageCircle, color: "#25D366" },
  email: { icon: Mail, color: "var(--amber)" },
  voice: { icon: Phone, color: "var(--red-2)" },
  otp: { icon: KeyRound, color: "var(--taupe)" },
  checkin: { icon: QrCode, color: "var(--green)" },
  referral: { icon: Handshake, color: "var(--amber)" },
  audit: { icon: ShieldCheck, color: "var(--muted)" },
};
const STATUS = { ok: "var(--green)", warn: "var(--amber)", fail: "var(--red-2)" };
const when = (iso) => new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
const inr = (p) => `₹${((p || 0) / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export function MemberDetailModal({ regId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!regId) { setData(null); setErr(""); return; }
    adminApi.get(`/admin/registrations/${regId}/detail`).then((r) => setData(r.data)).catch((e) => setErr(apiErr(e)));
  }, [regId]);

  const reg = data?.registration;
  const photo = reg?.photo_url ? (reg.photo_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${reg.photo_url}` : reg.photo_url) : null;
  const fields = reg ? [
    ["Phone", reg.phone], ["Email", reg.email], ["Region", reg.region], ["Chapter", reg.chapter],
    ["Category", reg.business_category], ["City", reg.city], ["Type", reg.registration_type],
    ["Tier", data.tier?.name], ["Status", reg.status], ["Invoice", reg.invoice_number || data.invoice?.number],
    ["Registered", reg.created_at && when(reg.created_at)], ["Paid at", reg.paid_at && when(reg.paid_at)],
    ["Registration ID", reg.id],
    ...Object.entries(reg.custom_fields || {}).map(([k, v]) => [k.replace(/_/g, " "), String(v)]),
  ].filter(([, v]) => v) : [];

  return (
    <Dialog open={!!regId} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="!bg-[var(--panel)] !border-[var(--hairline)] !rounded-none max-w-[860px] max-h-[90vh] overflow-y-auto p-0" data-testid="member-detail-modal">
        <div className="p-6">
          {!data && !err && <div className="mono-label py-10 text-center"><Loader2 size={14} className="inline animate-spin mr-2" />Loading member…</div>}
          {err && <div className="mono-label py-10 text-center" style={{ color: "var(--red-2)" }} data-testid="member-detail-error">{err}</div>}
          {data && (
            <>
              <DialogHeader>
                <div className="flex items-start gap-4">
                  {photo ? <img src={photo} alt="" className="w-16 h-16 object-cover border border-[var(--hairline)]" />
                    : <div className="w-16 h-16 border border-[var(--hairline)] bg-[var(--panel-2)] flex items-center justify-center font-serif text-xl" style={{ color: "var(--red-2)" }}>
                        {(reg.name || "·").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("")}
                      </div>}
                  <div className="min-w-0">
                    <DialogTitle className="font-serif text-2xl !text-[var(--text)]" data-testid="member-detail-name">{reg.name}</DialogTitle>
                    <DialogDescription className="mono-label mt-1 !text-[var(--taupe)]">
                      {[reg.registration_type, reg.chapter, reg.region].filter(Boolean).join(" · ")}
                    </DialogDescription>
                    {data.payment && (
                      <div className="mt-2 inline-flex items-center gap-2 chip" style={{ color: "var(--green)", borderColor: "var(--green)" }}>
                        <CreditCard size={11} /> PAID {inr(data.payment.amount_paise)}
                      </div>
                    )}
                  </div>
                  {reg.status === "paid" && (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${reg.id}.pdf`}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-ghost ml-auto !py-1.5 !px-3 text-xs"
                      data-testid="member-detail-invoice"
                      title={data.invoice?.number ? `Invoice ${data.invoice.number}` : "Generate and open invoice"}
                    >
                      <FileText size={12} /> Invoice PDF
                    </a>
                  )}
                </div>
              </DialogHeader>

              <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-0 hairline" data-testid="member-detail-fields">
                {fields.map(([k, v]) => (
                  <div key={k} className="p-3 border-b border-r border-[var(--hairline)] min-w-0">
                    <div className="mono-label" style={{ color: "var(--taupe)" }}>{k}</div>
                    <div className="text-sm mt-1 break-all">{v}</div>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex items-center justify-between">
                <div className="mono-label" style={{ color: "var(--taupe)" }}>ACTIVITY TIMELINE · {data.timeline.length} EVENTS</div>
              </div>
              <ol className="mt-3 relative" data-testid="member-detail-timeline">
                <span className="absolute left-[11px] top-2 bottom-2 w-px" style={{ background: "var(--hairline)" }} />
                {data.timeline.length === 0 && <li className="mono-label py-6 pl-8" style={{ color: "var(--taupe)" }}>No activity recorded yet.</li>}
                {data.timeline.map((t, i) => {
                  const K = KIND[t.kind] || KIND.audit; const Icon = K.icon;
                  return (
                    <li key={i} className="relative pl-9 py-2.5" data-testid={`timeline-item-${i}`}>
                      <span className="absolute left-0 top-2.5 w-6 h-6 flex items-center justify-center border border-[var(--hairline)]" style={{ background: "var(--panel)", color: K.color }}>
                        <Icon size={12} />
                      </span>
                      <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <div className="text-sm" style={{ color: STATUS[t.status] && t.status !== "ok" ? STATUS[t.status] : "var(--text)" }}>{t.title}</div>
                        <div className="mono-label" style={{ color: "var(--taupe)" }}>{when(t.ts)}</div>
                      </div>
                      {t.detail && <div className="mono-label mt-0.5 break-words" style={{ color: "var(--body)" }}>{t.detail}</div>}
                    </li>
                  );
                })}
              </ol>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
